import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { TreeNode } from 'primeng/api';

@Injectable({
  providedIn: 'root'
})
export class BusinessStateService {
  private readonly STORAGE_KEY = 'selectedBusiness';
  private readonly selectedBusinessSubject = new BehaviorSubject<TreeNode | null>(null);
  public selectedBusiness$: Observable<TreeNode | null> = this.selectedBusinessSubject.asObservable();
  
  private readonly refreshBusinessListSubject = new Subject<void>();
  public refreshBusinessList$: Observable<void> = this.refreshBusinessListSubject.asObservable();

  constructor() {
    this.restoreSelection();
  }

  /**
   * Actualiza la selección de negocio (grupo/empresa/sede)
   * @param business La selección del TreeSelect
   */
  setSelectedBusiness(business: TreeNode | null): void {
    this.selectedBusinessSubject.next(business);
    this.saveSelection(business);
  }

  getSelectedBusiness(): TreeNode | null {
    return this.selectedBusinessSubject.value;
  }

  isOfficeSelected(): boolean {
    const selected = this.selectedBusinessSubject.value;
    return this.normalizeKind(selected?.data) === 'office';
  }

  /**
   * Observable que indica si está seleccionada una sede
   * @returns Observable<boolean>
   */
  isOfficeSelected$(): Observable<boolean> {
    return new Observable(subscriber => {
      this.selectedBusiness$.subscribe(selected => {
        subscriber.next(this.normalizeKind(selected?.data) === 'office');
      });
    });
  }

  /**
   * Guarda la selección actual en localStorage
   * @param business La selección a guardar
   */
  private saveSelection(business: TreeNode | null): void {
    if (business) {
      // Solo guardar datos esenciales para reconstruir el TreeNode
      const normalizedKind = this.normalizeKind(business.data);
      const savedData = {
        id: business.data?.id,
        kind: normalizedKind,
        name: business.data?.name,
        label: business.label
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(savedData));
    }
  }

  /**
   * Restaura la selección guardada desde localStorage
   */
  private restoreSelection(): void {
    const savedData = localStorage.getItem(this.STORAGE_KEY);
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      // Crear un TreeNode temporal con los datos guardados
      // Este será reemplazado cuando el header cargue los datos reales
      const tempNode: TreeNode = {
        label: parsedData.label,
        data: {
          id: parsedData.id,
          kind: parsedData.kind,
          name: parsedData.name
        }
      };
      this.selectedBusinessSubject.next(tempNode);
    }
    else {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * Limpia la selección persistida
   */
  clearPersistedSelection(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.selectedBusinessSubject.next(null);
  }

  private normalizeKind(data: any): string | undefined {
    return data?.kind?.toString().toLowerCase() ?? data?.type?.toString().toLowerCase();
  }

  /**
   * Notifica que se debe refrescar el listado de empresas en el header
   */
  refreshBusinessList(): void {
    this.refreshBusinessListSubject.next();
  }
}

